// The customer profile's "Salik & parking" tab: Salik, Darb and parking of one contract.
// Every trip takes THE ENGINE's own state (the-engine/customerDue.logic tripState / notOnEjarOf - owner, 28 Sep 2026):
// "On Ejar" only when Ejar really holds the line, "Unlimited Salik" for a trip the contract's package covers (never
// "On Ejar"), else "Not on Ejar" - and a trip is charged its gate + AED 1.05 (TRIP_SURCHARGE), as everywhere; a covered
// trip is charged nothing (Branch charges' 0.00).

import { TRIP_SURCHARGE } from '../constants/finance';
import { TRIP_STATES, notOnEjarOf, salikTotalOf, tripState, unlimitedOf } from '../pages/the-engine/customerDue.logic';

export const KINDS = [
    { kind: 'toll', label: 'Salik', tone: 'accent' },
    { kind: 'darb', label: 'Darb', tone: 'info' },
    { kind: 'parking', label: 'Parking', tone: 'warning' },
];

const when = (r) => String(r.trip_time || r.entry_time || '').replace('T', ' ').slice(0, 16);

/**
 * The engine's one answer for the contract (/api/salik/contract-rows: {kinds: {toll, darb, parking}}, each
 * what /api/salik/rows answered for that kind) -> one settled result per kind, as the three separate
 * calls gave (audit DUP-18: they read the same Ejar contract three times). A failed call fails every kind.
 */
export function settleKinds(answer, error) {
    return KINDS.map(({ kind }) => (error
        ? { status: 'rejected', reason: error }
        : { status: 'fulfilled', value: (answer && answer.kinds && answer.kinds[kind]) || {} }));
}

/** The tab's figures from the settled kinds: every row (newest first), days never pulled, Ejar checked. */
export function mergeKinds(got, describe = (e) => String(e && e.message ? e.message : e)) {
    const rows = [];
    let missing = 0;
    let ejarChecked = true;
    const errors = [];
    got.forEach((g, i) => {
        if (g.status !== 'fulfilled') { if (KINDS[i].kind !== 'darb') errors.push(`${KINDS[i].label}: ${describe(g.reason)}`); return; }
        const d = g.value || {};
        (d.rows || []).forEach((r) => rows.push({ ...r, _kind: KINDS[i].kind }));
        missing = Math.max(missing, (d.coverage?.missing || []).length);
        if (d.ejarCheck && d.ejarCheck.checked === false) ejarChecked = false;
    });
    rows.sort((a, b) => when(b).localeCompare(when(a)));
    const note = got.map((g) => g.value?.note).find(Boolean);
    return { rows, missing, ejarChecked, note, errors };
}

// --- The three states and the money (owner, 28 Sep 2026) -----------------------------------------------------------

/** The contract as The Engine's customer tab holds one, so customerDue.logic reads it unchanged. `unl` is the
 *  contract's Unlimited Salik answer (true / false / null = unknown). */
const engineContract = (rows, unl, salikTotal = null) => ({
    unlimitedSalik: unl === true ? true : unl === false ? false : undefined,
    salikTotal,         // the engine's Salik total (contract-rows salikTotal) - The Engine's own Salik figure
    toll: { rows: (rows || []).filter((r) => r._kind === 'toll') },
    darb: { rows: (rows || []).filter((r) => r._kind === 'darb') },
    parking: { rows: (rows || []).filter((r) => r._kind === 'parking') },
});

/**
 * The contract's ONE Unlimited Salik answer from the engine's /api/salik/contract-rows: its `unlimitedSalik` (the
 * calculation the reconcile just read, else THE rule) and the reconcile's own answer per kind (ejarCheck) - the
 * same reads The Engine's unlimitedOf takes. true / false / null (null: an engine from before the answer).
 */
export function unlimitedOfAnswer(answer) {
    const kinds = (answer && answer.kinds) || {};
    return unlimitedOf({
        unlimitedSalik: answer ? answer.unlimitedSalik : undefined,
        ejarCheck: { toll: kinds.toll && kinds.toll.ejarCheck, darb: kinds.darb && kinds.darb.ejarCheck },
    });
}

/** One row's state: a Salik / Darb trip by The Engine's tripState; parking is on Ejar only when registered there. */
export function rowStateOf(r, unl) {
    if (r && (r._kind === 'toll' || r._kind === 'darb')) return tripState(r, unl);
    return r && r.reg_status === 'registered' ? 'on_ejar' : 'not_on_ejar';
}

/** The row's state as the table shows it: { key, label, variant } (TRIP_STATES - the Branch charges words). */
export const rowStatusOf = (r, unl) => {
    const key = rowStateOf(r, unl);
    return { key, ...TRIP_STATES[key] };
};

/** The row's own amount: a trip's gate, a parking's portal amount (the table's "Amount", Branch charges' too). */
export const amountOf = (r) => Number(r && r.amount) || 0;

/**
 * What the customer is charged for a row under the contract's answer `unl`: a trip = its gate + AED 1.05
 * (TRIP_SURCHARGE), but NOTHING for a trip covered by Unlimited Salik - Branch charges' own rule
 * (branch_charges._set_gate_state: charge 0.00); parking = its amount. It added 1.05 to a covered trip too, so the
 * profile said AED 5.05 "charged" where Branch charges said 0.00 for the same trip (review, owner 28 Sep 2026).
 */
export const chargeOf = (r, unl, tripSurcharge = TRIP_SURCHARGE) => {
    const amount = amountOf(r);
    if (!r || (r._kind !== 'toll' && r._kind !== 'darb')) return amount;
    return tripState(r, unl) === 'unlimited' ? 0 : amount + tripSurcharge;
};

const r2 = (n) => Math.round(n * 100) / 100;

/**
 * One contract's figures: per kind { n, charge } (charge = gate + 1.05 a trip, nothing for a covered one - chargeOf),
 * and per state { n, aed } - the "Not on Ejar yet" money is The Engine's notOnEjarOf (Salik + Darb + parking, AED 1.05
 * on every trip), so the profile and The Engine's "Total to collect" never disagree. `salikTotal` = the engine's
 * Salik total (/api/salik/contract-rows): when it is there, Salik + Darb not on Ejar = what it says is not added
 * (owner, 1 Oct 2026: "سالك ايا كان تاريخه 50 والصح يكون 70 يبقى فى 20 مش متضافة").
 */
export function contractFigures(rows, unl, tripSurcharge = TRIP_SURCHARGE, salikTotal = null) {
    const list = rows || [];
    const kinds = {};
    KINDS.forEach(({ kind }) => {
        const of = list.filter((r) => r._kind === kind);
        kinds[kind] = { n: of.length, charge: r2(of.reduce((a, r) => a + chargeOf(r, unl, tripSurcharge), 0)) };
    });
    const states = { on_ejar: { n: 0, aed: 0 }, unlimited: { n: 0, aed: 0 }, not_on_ejar: { n: 0, aed: 0 } };
    list.forEach((r) => {
        const s = states[rowStateOf(r, unl)];
        s.n += 1;
        s.aed += chargeOf(r, unl, tripSurcharge);
    });
    Object.values(states).forEach((s) => { s.aed = r2(s.aed); });
    const contract = engineContract(list, unl, salikTotal);
    const owed = notOnEjarOf(contract, null, tripSurcharge);
    states.not_on_ejar.aed = r2(owed.salik + owed.darb + owed.park);
    return { kinds, states, unlimited: unl === true, salikTotal: salikTotalOf(contract, null) };
}

/**
 * Why the engine could not match the contract to the car(s) it held, so its trips are UNKNOWN (never "none"), else
 * '': the contract-rows answer's note ("contract not found in the contract files / E-Check data" - the hourly files
 * lag a new contract), no car period at all (every kind's `windows` empty), or no kinds in the answer. Such a contract
 * was shown as a green "0 not on Ejar yet" and counted in the whole customer's total (review, owner 28 Sep 2026).
 */
export function unmatchedOf(answer) {
    const kinds = Object.values((answer && answer.kinds) || {}).filter(Boolean);
    if (!kinds.length) return 'the engine gave no Salik, Darb or parking answer for this contract';
    const note = kinds.map((k) => k.note).find(Boolean);
    if (note) return note;
    if (kinds.every((k) => Array.isArray(k.windows) && k.windows.length === 0)) return 'no car period found for this contract';
    return '';
}

/**
 * The whole customer from the contracts read so far: { read, total, complete, unmatched, left, notOnEjarN,
 * notOnEjarAed, unlimitedN }. `figures` = contract -> contractFigures(...) of the contracts read; `contracts` = every
 * contract of the customer; `unmatched` = contract -> why it could not be matched (unmatchedOf) - read, but its
 * trips unknown, so never in the total; `left` = the contracts still to read (not read, or their read failed).
 */
export function customerTotals(contracts, figures, unmatched = {}) {
    const all = (contracts || []).filter(Boolean);
    const read = all.filter((cn) => figures && figures[cn]);
    const unknown = all.filter((cn) => !(figures && figures[cn]) && unmatched && unmatched[cn]);
    const sum = (pick) => read.reduce((a, cn) => a + pick(figures[cn]), 0);
    return {
        read: read.length,
        total: all.length,
        complete: read.length === all.length,
        unmatched: unknown.length,
        left: all.length - read.length - unknown.length,
        notOnEjarN: sum((f) => f.states.not_on_ejar.n),
        notOnEjarAed: r2(sum((f) => f.states.not_on_ejar.aed)),
        unlimitedN: sum((f) => f.states.unlimited.n),
    };
}
